import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { KIcon } from '../shared/components/KIcon.js';
import { useNavigatie, useNavBadges } from './Sidebar.js';
import { isActiefPad } from './navigatie.js';

/**
 * Mobiele voet (<768, Kordaro v4): 72 px + safe-area, max 4 vaste bestemmingen + "Meer".
 * "Meer" bevat ALLE overige modules waarvoor de rol een menu-item had (niets verdwijnt).
 */
export function BottomNav() {
  const location = useLocation();
  const nav = useNavigatie();
  const badge = useNavBadges();
  const [meerOpen, setMeerOpen] = useState(false);
  const meerKnop = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);

  const meerActief = nav.meer.some((d) => isActiefPad(location.pathname, d.pad));

  useEffect(() => { setMeerOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!meerOpen) return;
    sheet.current?.querySelector<HTMLElement>('a,button')?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMeerOpen(false); meerKnop.current?.focus(); } };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [meerOpen]);

  return (
    <>
      <nav aria-label="Hoofdnavigatie" className="k-mobile-nav">
        {nav.voet.map((d) => {
          const actief = isActiefPad(location.pathname, d.pad);
          const n = badge(d);
          return (
            <Link key={d.pad} to={d.pad} aria-current={actief ? 'page' : undefined} className="k-mobile-nav__item">
              <KIcon naam={d.icoon} size={24} />
              <span className="max-w-full text-center [hyphens:auto] [overflow-wrap:anywhere]">{d.label}</span>
              {n > 0 && (
                <span className="absolute top-2 left-1/2 ml-2 min-w-[20px] px-1 rounded-full bg-brand text-[13px] leading-5 font-semibold text-center" style={{ color: 'var(--k-on-brand)' }}>
                  {n > 99 ? '99+' : n}<span className="k-sr-only"> ongelezen</span>
                </span>
              )}
            </Link>
          );
        })}
        <button
          ref={meerKnop}
          type="button"
          onClick={() => setMeerOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={meerOpen}
          aria-current={meerActief ? 'page' : undefined}
          className="k-mobile-nav__item"
        >
          <KIcon naam="meer" size={24} />
          <span>Meer</span>
        </button>
      </nav>

      {meerOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="k-meer-titel">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMeerOpen(false)} aria-hidden="true" />
          <div ref={sheet} className="absolute bottom-0 left-0 right-0 max-h-[85dvh] overflow-y-auto bg-white rounded-t-2xl shadow-xl pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between px-4 py-2 border-b border-card-border">
              <h2 id="k-meer-titel" className="k-section-title">Meer</h2>
              <button type="button" onClick={() => { setMeerOpen(false); meerKnop.current?.focus(); }} className="k-button k-button--icon" aria-label="Sluiten">
                <KIcon naam="sluit" />
              </button>
            </div>
            <ul className="p-2">
              {nav.meer.map((d) => {
                const actief = isActiefPad(location.pathname, d.pad);
                const n = badge(d);
                return (
                  <li key={d.pad}>
                    <Link to={d.pad} aria-current={actief ? 'page' : undefined} className="k-nav-link">
                      <KIcon naam={d.icoon} />
                      <span className="text-base">{d.label}</span>
                      {n > 0 && <span className="k-nav-badge">{n > 99 ? '99+' : n}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
